import React from 'react';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { OrderPageData } from '../../../api';
import { useLanguage } from '../../../utils/GeneralUtils';
import { useAccountData } from '../../StorefrontThemeWrapper';
import { getPrimaryPhoto } from '../../../utils/orderPageImageUtils';
import { useStartingPriceLabel } from '../../common/storefrontPriceUtils';

interface RelatedExperienceCardProps {
  orderPage: OrderPageData;
}

export const RelatedExperienceCard = ({ orderPage }: RelatedExperienceCardProps) => {
  const { accountId, storefrontId } = useParams();
  const [searchParams] = useSearchParams();
  const account = useAccountData();
  const theme = useTheme();
  const language = useLanguage();
  const { label: priceLabel, startingPrice } = useStartingPriceLabel(orderPage, account);
  const photo = getPrimaryPhoto(orderPage, 200);
  const ChevronIcon = theme.direction === 'rtl' ? ChevronLeft : ChevronRight;

  return (
    <Link
      to={{
        pathname: `/experience/${accountId}/${orderPage.id}/${storefrontId || ''}`,
        search: searchParams.toString(),
      }}
      style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
      <Grid
        container
        alignItems="center"
        wrap="nowrap"
        gap={1.5}
        sx={{
          padding: 1,
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          transition: 'background-color 0.2s',
          '&:hover': { backgroundColor: 'action.hover' },
        }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            flexShrink: 0,
            borderRadius: 1.5,
            overflow: 'hidden',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          {photo ? (
            <img
              src={photo}
              alt={orderPage.label?.[language] || ''}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Typography sx={{ opacity: 0.3, fontSize: 28 }}>🎨</Typography>
          )}
        </Box>
        <Grid container flexDirection="column" sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {orderPage.label?.[language]}
          </Typography>
          {startingPrice > 0 && !searchParams.has('hp') && (
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
              {priceLabel}
            </Typography>
          )}
        </Grid>
        <ChevronIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
      </Grid>
    </Link>
  );
};
